import { DocumentTypes, Vendor } from "@/prisma-package/src";
import {
  DocumentExtractedData,
  DocumentTypeExtractionField,
} from "@/prisma-package/src/utils/document-type-builtins";
import Anthropic from "@anthropic-ai/sdk";
import { z } from "zod";
import { sanitizeExtractedFields } from "./extraction-fields.utils";

interface ExtractedData {
  documentId: string | null;
  extractedVendorName: string | null;
  // Every company/organization name found in the document, primary vendor
  // first, deduped. Stored on the document so correction UIs can re-match
  // them against the current vendor list.
  vendorCandidates: string[];
  vendorMatches: Array<{
    vendorId: string;
    vendorName: string;
    confidence: number;
  }>;
  // Values for the type's admin-defined extraction fields, keyed by field
  // key — sanitized against the spec, all strings.
  fields: DocumentExtractedData;
}

interface AIExtractionResult {
  vendorName?: string | null;
  documentId?: string | null;
  otherVendorNames?: string[];
  // Sanitized custom field values (spec keys only, normalized strings).
  fields: DocumentExtractedData;
}

// The tool_use input straight from the model, before sanitization. Unknown
// keys are ignored by zod; custom field values may come back as numbers or
// booleans and are stringified by sanitizeExtractedFields.
const aiExtractionResultSchema = z.object({
  vendorName: z.string().nullish(),
  documentId: z.string().nullish(),
  otherVendorNames: z.array(z.string()).nullish(),
  fields: z
    .record(z.union([z.string(), z.number(), z.boolean(), z.null()]))
    .nullish(),
});

type RawExtractionResult = z.infer<typeof aiExtractionResultSchema>;

// Minimal vendor shape the matchers need; satisfied by the Prisma Vendor and
// by the lazy/dropdown vendor shapes used client-side (aliases optional).
export type MatchableVendor = {
  id: string;
  name: string;
  aliases?: string[] | null;
};

export interface DocumentExtractionInput {
  // Cloudinary delivery URL of the uploaded file; sent to the AI as the
  // document itself so extraction works on scans and stylized letterheads
  // that OCR misses.
  secureUrl: string;
  // OCR/embedded text, used as a fallback when the file can't be sent
  // (unsupported format, too many pages, transient API error).
  documentText?: string | null;
  // Document type key, when known. Some types change what counts as the
  // document ID (e.g. W9s use the EIN).
  documentType?: string | null;
  // The type's admin-defined extraction fields; each becomes a prompt
  // instruction and a tool-schema property.
  extractionFields?: DocumentTypeExtractionField[] | null;
}

// Per-type overrides for what "vendor" and "document ID" mean. W9s are forms,
// not invoices: the vendor is the taxpayer named on the form (never the IRS),
// and the identifier we want is the EIN from Part I — written one digit per
// box. SSNs must never be stored, so a W9 with only an SSN gets no ID.
const INSTRUCTIONS_BY_TYPE: Record<
  string,
  { vendorInstruction?: string; documentIdInstruction?: string }
> = {
  [DocumentTypes.W9]: {
    vendorInstruction:
      'Vendor/Company Name: This is an IRS Form W-9. Return the name the form is filled out for: the business name on Line 2 if present, otherwise the name on Line 1. Never return "Internal Revenue Service" or "Department of the Treasury" — those are the form publisher, not the vendor.',
    documentIdInstruction:
      "Document ID: The EIN (Employer Identification Number) from Part I, formatted XX-XXXXXXX. The form has two rows of boxes: an SSN row (3-2-4 digits) and an EIN row (2-7 digits), each digit in its own box — read the digits of whichever row is filled in, left to right. Only return the number when it is in the EIN row; if only the SSN row is filled in or no TIN is present, return null — never return an SSN or any part of one.",
  },
  [DocumentTypes.BUSINESS_LICENSE]: {
    vendorInstruction:
      'Vendor/Company Name: This is a business license or registration certificate. Return the business the license is issued TO — the "Business Name", licensee, DBA, or owner named on the certificate. Never return the issuing authority: no city, county, or state name, and no government agency, department, or registration division.',
    documentIdInstruction:
      "Document ID: The license, certificate, or registration number printed on the document.",
  },
};

const DEFAULT_VENDOR_INSTRUCTION =
  "Vendor/Company Name: The primary vendor or company issuing this document (invoice, receipt, etc.)";

const DEFAULT_DOCUMENT_ID_INSTRUCTION =
  "Document ID: Any invoice number, PO number, reference number, or document identifier";

// Appended to each custom field's instruction so values come back in a shape
// normalizeFieldValue can keep as-is.
const FORMAT_RULES_BY_DATA_TYPE: Record<string, string> = {
  text: "",
  number: " Return only digits and an optional decimal point.",
  date: " Return the date in YYYY-MM-DD format.",
  currency:
    " Return the numeric amount only — no currency symbol or thousands separators (e.g. 1234.56).",
};

/**
 * Build the extraction prompt, tailoring the vendor and document-ID
 * instructions to the document type when it changes what they mean (e.g. a
 * W9's vendor is the taxpayer on the form and its ID is the EIN), and
 * appending one numbered instruction per admin-defined extraction field.
 */
export function buildExtractionInstructions(
  documentType?: string | null,
  extractionFields?: DocumentTypeExtractionField[] | null
): string {
  const overrides = (documentType && INSTRUCTIONS_BY_TYPE[documentType]) || {};
  const vendorInstruction =
    overrides.vendorInstruction ?? DEFAULT_VENDOR_INSTRUCTION;
  const documentIdInstruction =
    overrides.documentIdInstruction ?? DEFAULT_DOCUMENT_ID_INSTRUCTION;

  const customItems = (extractionFields ?? []).map((field, index) => {
    const instruction = field.instruction?.trim() || `The ${field.label.trim()} of the document.`;
    const formatRule = FORMAT_RULES_BY_DATA_TYPE[field.dataType] ?? "";
    return `${index + 4}. ${field.label.trim()} ("${field.key}"): ${instruction}${formatRule}`;
  });

  return `Analyze this document and extract the following information:

1. ${vendorInstruction}
2. ${documentIdInstruction}
3. Other Companies: any other company or organization names that appear in the document (e.g., the bill-to/customer, remit-to, payee, parent company). One of these may actually be the vendor if the primary is ambiguous. Up to 5, excluding the primary vendor. Do not include people's names.
${customItems.length > 0 ? `${customItems.join("\n")}\n` : ""}
Record the results with the record_extracted_data tool, using null for anything not found. Never return a Social Security Number or any part of one, for any field.`;
}

/**
 * Build the forced-choice tool whose input schema carries the extraction
 * contract: the three fixed properties plus the admin-defined fields nested
 * under `fields` (nesting keeps custom keys from ever colliding with the
 * fixed ones). Exported for tests.
 */
export function buildExtractionTool(
  extractionFields?: DocumentTypeExtractionField[] | null
): Anthropic.Tool {
  const fields = extractionFields ?? [];
  return {
    name: "record_extracted_data",
    description: "Record the data extracted from the document.",
    input_schema: {
      type: "object" as const,
      properties: {
        vendorName: {
          type: ["string", "null"],
          description: "The primary vendor/company name, or null if not found",
        },
        documentId: {
          type: ["string", "null"],
          description: "The document identifier, or null if not found",
        },
        otherVendorNames: {
          type: "array",
          items: { type: "string" },
          description:
            "Other company or organization names found in the document",
        },
        ...(fields.length > 0 && {
          fields: {
            type: "object",
            description:
              "The numbered custom fields from the instructions, keyed exactly by their quoted keys. Use null for any field not found.",
            properties: Object.fromEntries(
              fields.map((field) => [
                field.key,
                {
                  type: ["string", "null"],
                  description: `${field.label.trim()}${
                    field.instruction?.trim()
                      ? `: ${field.instruction.trim()}`
                      : ""
                  }`.slice(0, 300),
                },
              ])
            ),
          },
        }),
      },
      required: ["vendorName", "documentId", "otherVendorNames"],
    },
  };
}

const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp"]);

/**
 * Build the content block that attaches the uploaded file to the AI request.
 * Only files served from our own Cloudinary account are sent (same rule as
 * extractDocumentTextAction). Returns null for anything else so callers fall
 * back to the text prompt.
 */
export function buildFileContentBlock(
  secureUrl: string
): Anthropic.ContentBlockParam | null {
  let url: URL;
  try {
    url = new URL(secureUrl);
  } catch {
    return null;
  }

  const cloudName = process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME;
  if (
    url.protocol !== "https:" ||
    url.hostname !== "res.cloudinary.com" ||
    !url.pathname.startsWith(`/${cloudName}/`)
  ) {
    return null;
  }

  const extension = url.pathname.split(".").pop()?.toLowerCase() ?? "";
  if (IMAGE_EXTENSIONS.has(extension)) {
    return {
      type: "image",
      source: { type: "url", url: secureUrl },
    };
  }

  // PDFs and anything else go as a document; an unsupported format fails the
  // API call and the caller falls back to the text prompt.
  return {
    type: "document",
    source: { type: "url", url: secureUrl },
  };
}

// Fallback for the (rare) case that a forced tool_choice response carries no
// tool_use block: slice the outermost JSON object out of the text response.
const parseTextFallback = (text: string): unknown => {
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start < 0 || end <= start) return null;
  try {
    return JSON.parse(text.slice(start, end + 1));
  } catch {
    return null;
  }
};

async function runExtraction(
  anthropic: Anthropic,
  content: Anthropic.ContentBlockParam[],
  tool: Anthropic.Tool
): Promise<RawExtractionResult> {
  // Haiku 4.5 does not think by default, so forced tool_choice needs no
  // thinking-disable cast (see classifier-grouping.utils.ts).
  const message = await anthropic.messages.create({
    model: "claude-haiku-4-5-20251001",
    max_tokens: 2000,
    temperature: 0.1,
    tools: [tool],
    tool_choice: { type: "tool", name: tool.name },
    messages: [
      {
        role: "user",
        content,
      },
    ],
  });

  const toolBlock = message.content.find((block) => block.type === "tool_use");
  if (toolBlock && toolBlock.type === "tool_use") {
    const result = aiExtractionResultSchema.safeParse(toolBlock.input);
    if (result.success) return result.data;
    console.error(
      "[doc-extraction] tool input failed validation:",
      JSON.stringify(toolBlock.input).slice(0, 500),
      result.error.message
    );
  }

  const textBlock = message.content.find((block) => block.type === "text");
  if (textBlock && textBlock.type === "text") {
    const result = aiExtractionResultSchema.safeParse(
      parseTextFallback(textBlock.text)
    );
    if (result.success) return result.data;
  }

  throw new Error("Failed to parse AI response");
}

/**
 * Extract information from a document using AI. Sends the uploaded file
 * itself (PDF or image) and falls back to the OCR/embedded text when the
 * file can't be processed.
 */
// Text-fallback truncation. Generous on purpose: custom fields like totals
// and due dates often appear on the last page of a document.
export const EXTRACTION_TEXT_LIMIT = 15000;

export async function extractDocumentDataWithAI(
  input: DocumentExtractionInput
): Promise<AIExtractionResult> {
  const empty: AIExtractionResult = {
    vendorName: null,
    documentId: null,
    otherVendorNames: [],
    fields: {},
  };

  console.log(
    `[doc-extraction] start type=${input.documentType ?? "none"} customFields=${input.extractionFields?.length ?? 0} textLength=${input.documentText?.length ?? 0} url=${input.secureUrl}`
  );

  const finalize = (raw: RawExtractionResult): AIExtractionResult => ({
    vendorName: raw.vendorName || null,
    documentId: raw.documentId || null,
    otherVendorNames: (raw.otherVendorNames ?? []).filter(
      (name) => name.trim().length > 0
    ),
    fields: sanitizeExtractedFields(input.extractionFields, raw.fields ?? {}),
  });

  try {
    if (!process.env.ANTHROPIC_API_KEY) {
      throw new Error("AI service not configured");
    }

    const anthropic = new Anthropic({
      apiKey: process.env.ANTHROPIC_API_KEY,
    });

    const instructions = buildExtractionInstructions(
      input.documentType,
      input.extractionFields
    );
    const tool = buildExtractionTool(input.extractionFields);

    const fileBlock = buildFileContentBlock(input.secureUrl);
    if (!fileBlock) {
      console.log(
        "[doc-extraction] file not sendable (non-Cloudinary or bad URL), using text fallback"
      );
    }
    if (fileBlock) {
      try {
        const result = finalize(
          await runExtraction(
            anthropic,
            [fileBlock, { type: "text", text: instructions }],
            tool
          )
        );
        console.log(
          `[doc-extraction] file extraction done documentId=${result.documentId ?? "null"} vendorName=${result.vendorName ?? "null"} fields=${Object.keys(result.fields).length}`
        );
        return result;
      } catch (fileError) {
        // Fall through to the text fallback below.
        console.error(
          "[doc-extraction] file extraction failed, falling back to text:",
          fileError
        );
      }
    }

    const text = input.documentText ?? "";
    if (text.length < 10) {
      console.log(
        "[doc-extraction] no usable document text for fallback, returning empty"
      );
      return empty;
    }

    const prompt = `${instructions}

Document text:
"""
${text.substring(0, EXTRACTION_TEXT_LIMIT)} ${text.length > EXTRACTION_TEXT_LIMIT ? '...[truncated]' : ''}
"""`;

    const result = finalize(
      await runExtraction(anthropic, [{ type: "text", text: prompt }], tool)
    );
    console.log(
      `[doc-extraction] text extraction done documentId=${result.documentId ?? "null"} vendorName=${result.vendorName ?? "null"} fields=${Object.keys(result.fields).length}`
    );
    return result;
  } catch (error) {
    console.error("[doc-extraction] extraction failed:", error);
    return empty;
  }
}


/**
 * Dedupe extracted company names case-insensitively, primary first.
 */
export function buildVendorCandidates(
  vendorName: string | null | undefined,
  otherVendorNames: string[] | undefined
): string[] {
  const candidates: string[] = [];
  const seen = new Set<string>();
  for (const name of [vendorName, ...(otherVendorNames ?? [])]) {
    const trimmed = name?.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    candidates.push(trimmed);
  }
  return candidates;
}

// Below this confidence a match is too weak to act on without a human: the
// 0.5 word-overlap tier produces false positives like "New York ... Division"
// → "WE ARE YOUR DOL NEW YORK". Gated matches aren't lost — the extracted
// names stay in vendorCandidates, so correction UIs still offer them.
export const MIN_VENDOR_MATCH_CONFIDENCE = 0.7;

/**
 * Keep only matches confident enough to auto-associate or pre-select.
 */
export function filterConfidentVendorMatches<T extends { confidence: number }>(
  matches: T[]
): T[] {
  return matches.filter(
    (match) => match.confidence >= MIN_VENDOR_MATCH_CONFIDENCE
  );
}

/**
 * Extract all data from a document using AI
 */
export async function extractDocumentData(
  input: DocumentExtractionInput,
  vendors: Vendor[]
): Promise<ExtractedData> {
  // First, get AI extraction
  const aiResult = await extractDocumentDataWithAI(input);

  // Then match the extracted vendor name against our database
  let vendorMatches: Array<{ vendorId: string; vendorName: string; confidence: number; }> = [];

  if (aiResult.vendorName) {
    vendorMatches = filterConfidentVendorMatches(
      matchVendorName(aiResult.vendorName, vendors)
    );
  }

  const result = {
    documentId: aiResult.documentId || null,
    extractedVendorName: aiResult.vendorName || null,
    vendorCandidates: buildVendorCandidates(
      aiResult.vendorName,
      aiResult.otherVendorNames
    ),
    vendorMatches,
    fields: aiResult.fields,
  };

  return result;
}

/**
 * Match each extracted candidate name against the vendor list and return the
 * distinct vendors they resolve to (best confidence per vendor), excluding
 * any explicitly excluded vendor ids (e.g. the currently assigned vendor).
 * Used by correction UIs to offer one-click alternatives.
 */
export function matchVendorCandidates(
  candidateNames: string[],
  vendors: MatchableVendor[],
  opts?: { excludeVendorIds?: Array<string | null | undefined> }
): Array<{
  vendorId: string;
  vendorName: string;
  confidence: number;
  extractedName: string;
}> {
  const excluded = new Set(
    (opts?.excludeVendorIds ?? []).filter((id): id is string => !!id)
  );
  const byVendorId = new Map<
    string,
    { vendorId: string; vendorName: string; confidence: number; extractedName: string }
  >();

  for (const name of candidateNames) {
    const [match] = matchVendorName(name, vendors);
    if (!match || excluded.has(match.vendorId)) continue;
    const existing = byVendorId.get(match.vendorId);
    if (!existing || match.confidence > existing.confidence) {
      byVendorId.set(match.vendorId, { ...match, extractedName: name });
    }
  }

  return Array.from(byVendorId.values()).sort(
    (a, b) => b.confidence - a.confidence
  );
}

// Fold case, punctuation, and whitespace so "Acme Catering, Inc." and
// "Acme Catering Inc" compare equal. Punctuation-only differences between an
// extracted name and the stored vendor name are noise, not signal.
export const normalizeVendorNameForMatch = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[.,'’]/g, "")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Match a single vendor name against the vendor database
 */
export function matchVendorName(
  extractedName: string,
  vendors: MatchableVendor[]
): Array<{ vendorId: string; vendorName: string; confidence: number }> {
  if (!extractedName || !vendors.length) return [];

  const matches: Array<{ vendorId: string; vendorName: string; confidence: number }> = [];
  const extractedLower = normalizeVendorNameForMatch(extractedName);

  vendors.forEach(vendor => {
    let confidence = 0;
    const vendorNameLower = normalizeVendorNameForMatch(vendor.name);

    // Exact match
    if (vendorNameLower === extractedLower) {
      confidence = 1.0;
    }
    // Check aliases for exact match
    else if (vendor.aliases && vendor.aliases.length > 0) {
      const aliasMatch = vendor.aliases.find(alias =>
        normalizeVendorNameForMatch(alias) === extractedLower
      );
      if (aliasMatch) {
        confidence = 0.95;
      }
    }

    // If no exact match found, try fuzzy matching
    if (confidence === 0) {
      // Fuzzy matching - one contains the other
      if (vendorNameLower.includes(extractedLower) || extractedLower.includes(vendorNameLower)) {
        confidence = 0.7;
      }
      // Very fuzzy - significant word overlap
      else {
        const vendorWords = vendorNameLower.split(/\s+/);
        const extractedWords = extractedLower.split(/\s+/);
        const commonWords = vendorWords.filter(word =>
          extractedWords.includes(word) && word.length > 2
        );

        if (commonWords.length >= 2 ||
            (commonWords.length === 1 && commonWords[0].length > 4)) {
          confidence = 0.5;
        }
      }
    }

    if (confidence > 0) {
      matches.push({
        vendorId: vendor.id,
        vendorName: vendor.name,
        confidence,
      });
    }
  });

  // Sort by confidence and return only the best match
  const sortedMatches = matches.sort((a, b) => b.confidence - a.confidence);

  if (sortedMatches.length > 0) {
    const topMatch = sortedMatches[0];
    return [topMatch]; // Return only the best match
  }

  return [];
}
